import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState } from 'react';

import Error from '@/components/common/Error';
import { DialogFooter } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import Input from '@/components/form/Input';
import SpinnerButton from '@/components/common/SpinnerButton';

import { useChangePassword } from '@/data/user';

const formSchema = z
  .object({
    password: z.string().min(1, { message: 'Required' }),
    newPassword: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters' }),
    confirmPassword: z.string().min(1, { message: 'Required' })
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword']
  });

const PasswordForm = ({ setOpen }) => {
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const changePassword = useChangePassword();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      newPassword: '',
      confirmPassword: ''
    }
  });

  const onSubmit = async (values) => {
    setSubmitting(true);
    setApiError('');

    const payload = {
      password: values.password,
      newPassword: values.newPassword
    };

    try {
      await changePassword(payload);
      setSubmitting(false);
      setOpen(false);
    } catch (err) {
      setSubmitting(false);
      setApiError(err.message);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2 my-4">
        <div className="grid grid-cols-1 gap-2">
          <Input
            name="password"
            label="Current Password"
            type="password"
            autoComplete="current-password"
          />
          <Input
            name="newPassword"
            label="New Password"
            type="password"
            autoComplete="new-password"
          />
          <Input
            name="confirmPassword"
            label="Confirm New Password"
            type="password"
            autoComplete="new-password"
          />
        </div>

        {apiError && <Error error={apiError} />}
        <div className="pt-8">
          <DialogFooter>
            <SpinnerButton loading={submitting}>Change Password</SpinnerButton>
          </DialogFooter>
        </div>
      </form>
    </Form>
  );
};

export default PasswordForm;
